import * as React from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Compass, Maximize2, Radio, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { usePoll } from '@/hooks/use-realtime'
import { usePresence } from '@/hooks/use-presence'
import { driveTo, readRoute, sanitizeRoute, slideOf, type FrameWindow } from '@/lib/deck-nav'
import { displayName } from '@/lib/format'
import type { Activity, Presentation, PresentationSession, TeamMember } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ErrorNote, Loading } from '@/components/page'

/**
 * Full-screen deck. Rendered OUTSIDE AppShell — the sidebar and page chrome
 * would eat a third of the screen in a room with a projector.
 *
 * The deck is an iframe over the pre-built Slidev output at
 * /presentations/<slug>/, same origin, served by the same Caddy that serves
 * this SPA. It has to be an iframe rather than a route: each deck is its own
 * compiled Vue application with its own router, keybindings and styles, and
 * there is no sane way to mount 19 of those inside a React tree.
 *
 * Slidev keyboard nav (arrows, space, f, o) only reaches the deck while the
 * iframe has focus, so we focus it on mount — otherwise the presenter's first
 * arrow key does nothing and they have to click the slide first.
 *
 * ## Three roles, one screen
 *
 * Which one you get is decided by the session in the query string:
 *
 *   no `?session=`   solo. A rehearsal or a read-through. Nothing is published,
 *                    nothing is followed, and no attendance exists to end.
 *   presenter        `session.presenter_id` is you. Your slide position is
 *                    published to the room and written to the session row.
 *   follower         anyone else who opened the same session. Your deck is
 *                    driven from the presenter's position until you say
 *                    otherwise.
 *
 * Position travels over Realtime **presence** — the only push mechanism this
 * platform delivers (postgres_changes and broadcast are both dead here; the
 * measurements are in `hooks/use-presence.ts`). Presence has no history, so the
 * session row carries the same position for anyone who joins late or whose
 * socket is down. Fast path and safety net, the same bargain Discussion makes.
 */

/** What each participant publishes about themselves. A `type`, not an `interface`, so it satisfies `Record<string, unknown>`. */
type DeckPresence = {
  role: 'presenter' | 'follower'
  name: string
  /** Slidev route, e.g. `/7?clicks=3`. Only meaningful on the presenter. */
  route: string | null
}

/**
 * How often the presenter's own deck is read.
 *
 * Polling rather than a `hashchange` listener because Slidev's router navigates
 * through `history.pushState`, which fires no event a parent can hear — see
 * `lib/deck-nav.ts`. Reading `location.hash` off a same-origin window is a
 * string compare, so this is far cheaper than it looks, and it doubles as the
 * tick that holds a following deck on the presenter's slide.
 */
const TICK_MS = 250

/** Trailing debounce on the session row write. Presence already carries the live position; this is only for latecomers. */
const PERSIST_MS = 1_000

/**
 * How often the presenter says "still here" even when the slide hasn't changed.
 *
 * `live_presentation_sessions` ages a session out after ten minutes of silence,
 * because a closed tab leaves `ended_at` NULL forever and would otherwise
 * advertise a Join button for a meeting that finished in June. But silence is
 * also a completely normal state: a good discussion parks on one slide for
 * longer than ten minutes, and without a heartbeat that running session would
 * quietly stop being joinable. Comfortably inside the window, and one tiny PATCH
 * every two minutes.
 */
const HEARTBEAT_MS = 120_000

export function PresentPage() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const sessionId = params.get('session')
  const { session: auth, profile } = useAuth()
  const userId = auth?.user?.id ?? null
  const navigate = useNavigate()
  const frameRef = React.useRef<HTMLIFrameElement>(null)
  const wrapRef = React.useRef<HTMLDivElement>(null)

  /** Set on the iframe's load event: before it, `contentWindow` is about:blank and has no deck to read or drive. */
  const [frameReady, setFrameReady] = React.useState(false)

  /** This deck's own position, whichever role we are. */
  const [route, setRoute] = React.useState<string | null>(null)

  /** Followers only: false means "I've wandered off deliberately, stop dragging me back". */
  const [following, setFollowing] = React.useState(true)

  /** Position from the session row. The fallback when presence isn't delivering, and the answer for anyone joining mid-deck. */
  const [storedRoute, setStoredRoute] = React.useState<string | null>(null)

  const { data, loading, error } = useQuery(async () => {
    if (!id) return null
    const { data: act, error: aErr } = await sb()
      .from('activities')
      .select('*')
      .eq('id', id)
      .maybeSingle()
    if (aErr) throw aErr
    if (!act) return null
    const activity = act as Activity

    const [deck, attendees, sess] = await Promise.all([
      activity.presentation_id
        ? sb().from('presentations').select('*').eq('id', activity.presentation_id).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      // Names for the header strip, so the presenter can sanity-check who got
      // recorded without leaving the deck.
      sessionId
        ? sb()
            .from('presentation_attendees')
            .select('member_id, team_members(display_name, email)')
            .eq('session_id', sessionId)
        : Promise.resolve({ data: [], error: null }),
      // Decides the role, and carries the position for a late join.
      sessionId
        ? sb().from('presentation_sessions').select('*').eq('id', sessionId).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
    ])
    if (deck.error) throw deck.error
    if (attendees.error) throw attendees.error
    if (sess.error) throw sess.error

    type Joined = { member_id: string; team_members: Pick<TeamMember, 'display_name' | 'email'> | null }
    return {
      activity,
      deck: (deck.data ?? null) as Presentation | null,
      session: (sess.data ?? null) as PresentationSession | null,
      attendees: ((attendees.data ?? []) as Joined[]).map(
        (r) => r.team_members?.display_name || r.team_members?.email || 'Someone',
      ),
    }
  }, [id, sessionId])

  const sessionRow = data?.session ?? null
  // A session nobody can be measured against (missing row, or not signed in)
  // degrades to solo rather than guessing — better to present unsynced than to
  // have two people both believe they're driving.
  const synced = Boolean(sessionId && sessionRow && userId)
  const isPresenter = synced && sessionRow?.presenter_id === userId
  const isFollower = synced && !isPresenter

  const me = React.useMemo(
    () => displayName(profile ?? { display_name: null, email: auth?.user?.email ?? 'Someone' }),
    [profile, auth?.user?.email],
  )

  // ---------------------------------------------------------------------------
  // The live channel.
  //
  // Everyone in the session joins it, including the presenter — the presenter's
  // own entry is what followers read, and the followers' entries are how the
  // presenter sees the room. Keyed on user id so a remount replaces that
  // person rather than adding a ghost.
  // ---------------------------------------------------------------------------
  const presenceState = React.useMemo<DeckPresence>(
    () => ({ role: isPresenter ? 'presenter' : 'follower', name: me, route: isPresenter ? route : null }),
    [isPresenter, me, route],
  )

  const { peers, live } = usePresence<DeckPresence>({
    channel: `deck-session-${sessionId ?? 'none'}`,
    key: userId ?? 'anon',
    state: presenceState,
    enabled: synced,
  })

  const presenterPeer = peers.find((p) => p.role === 'presenter') ?? null
  const watchers = peers.filter((p) => p.key !== userId)
  const presenterName = presenterPeer?.name ?? null

  /** Where the room is: the presenter's live position, or the stored one if they aren't on the channel. */
  const target = sanitizeRoute(presenterPeer?.route ?? null) ?? storedRoute

  // ---------------------------------------------------------------------------
  // Followers: keep the session row as a fallback.
  //
  // Backed off hard once presence has actually delivered — not once it has
  // merely subscribed. See the note in use-realtime.ts about what trusting
  // SUBSCRIBED did to Discussion; `live` is the earned signal.
  // ---------------------------------------------------------------------------
  const refetchPosition = React.useCallback(async () => {
    if (!sessionId) return
    const { data: row } = await sb()
      .from('presentation_sessions')
      .select('current_hash')
      .eq('id', sessionId)
      .maybeSingle()
    setStoredRoute(sanitizeRoute((row as { current_hash: string | null } | null)?.current_hash))
  }, [sessionId])

  // Seeded from the row already fetched above, so a late joiner is on the right
  // slide immediately instead of after the first poll.
  React.useEffect(() => {
    setStoredRoute(sanitizeRoute(sessionRow?.current_hash))
  }, [sessionRow?.current_hash])

  usePoll(() => void refetchPosition(), live ? 20_000 : 3_000, isFollower)

  // ---------------------------------------------------------------------------
  // The tick. Reads our own deck, and — if following — holds it on the
  // presenter's slide.
  //
  // Both halves live in one interval on purpose: the snap-back has to run
  // against a position we just read, and splitting them would race.
  // ---------------------------------------------------------------------------
  const targetRef = React.useRef<string | null>(target)
  targetRef.current = target
  const followingRef = React.useRef(following)
  followingRef.current = following

  React.useEffect(() => {
    if (!frameReady) return
    const win = frameRef.current?.contentWindow as FrameWindow | null | undefined
    if (!win) return

    const tick = () => {
      // Drive first, then read, so `route` reflects where we ended up rather
      // than lagging a tick behind every time we're pulled forward.
      if (isFollower && followingRef.current) driveTo(win, targetRef.current)
      const now = readRoute(win)
      setRoute((prev) => (prev === now ? prev : now))
    }

    tick()
    const timer = window.setInterval(tick, TICK_MS)
    return () => window.clearInterval(timer)
  }, [frameReady, isFollower])

  // ---------------------------------------------------------------------------
  // A follower reaching for the arrow keys means it.
  //
  // Without this, a locked follower who presses Right gets dragged back within
  // 250ms and the deck reads as broken. Unlocking on the keypress is the signal
  // we can trust — a drive that merely failed produces no keydown, so this can't
  // be confused with one and silently unlock the room.
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (!frameReady || !isFollower) return
    const win = frameRef.current?.contentWindow as FrameWindow | null | undefined
    if (!win) return

    const NAV_KEYS = new Set([
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'PageUp', 'PageDown', ' ', 'Spacebar', 'Home', 'End',
    ])
    const onKey = (e: KeyboardEvent) => {
      if (NAV_KEYS.has(e.key)) setFollowing(false)
    }
    try {
      win.document.addEventListener('keydown', onKey)
    } catch {
      return
    }
    return () => {
      try {
        win.document.removeEventListener('keydown', onKey)
      } catch {
        // Frame already torn down; nothing to detach from.
      }
    }
  }, [frameReady, isFollower])

  // ---------------------------------------------------------------------------
  // Presenter: persist the position for latecomers.
  //
  // Trailing debounce — presence is already carrying the live position, so this
  // only needs to be right once the presenter stops moving. Holding an arrow key
  // through twenty slides writes one row, not twenty.
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (!isPresenter || !sessionId || !route) return
    const timer = window.setTimeout(() => {
      void sb()
        .from('presentation_sessions')
        .update({
          current_hash: route,
          current_slide: slideOf(route),
          slide_updated_at: new Date().toISOString(),
        })
        .eq('id', sessionId)
    }, PERSIST_MS)
    return () => window.clearTimeout(timer)
  }, [isPresenter, sessionId, route])

  // Presenter: "still here", so a long discussion on one slide doesn't drop the
  // room out of its own session. See HEARTBEAT_MS.
  React.useEffect(() => {
    if (!isPresenter || !sessionId) return
    const beat = () => {
      void sb()
        .from('presentation_sessions')
        .update({ slide_updated_at: new Date().toISOString() })
        .eq('id', sessionId)
        // Never resurrect a session that has already been ended — the pagehide
        // handler below may have won the race with this interval's last tick.
        .is('ended_at', null)
    }
    const timer = window.setInterval(beat, HEARTBEAT_MS)
    return () => window.clearInterval(timer)
  }, [isPresenter, sessionId])

  // Stamp ended_at when the presenter leaves — whether by the Exit button
  // (unmount) or by closing the tab (pagehide). Without the pagehide half,
  // every closed tab would leave a session that looks like it never finished.
  //
  // **Presenter only.** Followers hold the same `?session=` in their URL, so
  // without that guard the first person to duck out of the meeting would end the
  // session for the room — and 0020's RLS would reject their write anyway, so
  // the failure would be silent and only visible as a Join button that stopped
  // appearing.
  React.useEffect(() => {
    if (!sessionId || !isPresenter) return
    const finish = () => {
      void sb()
        .from('presentation_sessions')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', sessionId)
        .is('ended_at', null)
    }
    window.addEventListener('pagehide', finish)
    return () => {
      window.removeEventListener('pagehide', finish)
      finish()
    }
  }, [sessionId, isPresenter])

  React.useEffect(() => {
    if (data?.deck) frameRef.current?.focus()
  }, [data?.deck])

  if (loading && !data) return <Loading label="Opening the deck…" />

  if (error || !data) {
    return (
      <div className="mx-auto max-w-lg space-y-4 px-4 py-16">
        {error ? <ErrorNote error={error} /> : <p className="text-sm">That topic doesn’t exist.</p>}
        <Button variant="outline" asChild>
          <Link to="/activities">
            <ArrowLeft /> Back to topics
          </Link>
        </Button>
      </div>
    )
  }

  const { activity, deck, attendees } = data

  if (!deck) {
    return (
      <div className="mx-auto max-w-lg space-y-4 px-4 py-16">
        <h1 className="text-lg font-semibold">No deck for this topic</h1>
        <p className="text-sm text-muted-foreground">
          “{activity.title}” isn’t linked to a slide deck, so there’s nothing to present.
        </p>
        <Button variant="outline" asChild>
          <Link to={`/activities/${activity.id}`}>
            <ArrowLeft /> Back to the topic
          </Link>
        </Button>
      </div>
    )
  }

  const mySlide = slideOf(route)
  const targetSlide = slideOf(target)
  const behind = isFollower && !following && targetSlide !== null && targetSlide !== mySlide

  return (
    <div ref={wrapRef} className="flex h-screen flex-col bg-black">
      {/* Deliberately thin — every pixel here is a pixel off the slide. */}
      <div className="flex h-10 shrink-0 items-center gap-3 border-b border-white/10 bg-neutral-950 px-3 text-white">
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10 hover:text-white"
          onClick={() => navigate(`/activities/${activity.id}`)}
        >
          <ArrowLeft /> Exit
        </Button>
        <span className="min-w-0 truncate text-sm font-medium">{deck.title}</span>

        {/* Presenter: who is actually watching right now, which is a different
            question from who was recorded as attending. */}
        {isPresenter && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="muted" className="shrink-0 cursor-default gap-1 text-[10px]">
                <Radio className={watchers.length > 0 ? 'h-3 w-3 text-emerald-400' : 'h-3 w-3'} />
                {watchers.length === 0
                  ? 'Presenting'
                  : `${watchers.length} following`}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {watchers.length === 0 ? (
                <p className="text-xs">
                  Nobody has joined yet. They can join from the topic page while this is open.
                </p>
              ) : (
                <ul className="space-y-0.5 text-xs">
                  {watchers.map((w) => (
                    <li key={w.key}>{w.name}</li>
                  ))}
                </ul>
              )}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Follower: following, or off on their own with a way back. */}
        {isFollower && (
          <div className="flex shrink-0 items-center gap-2">
            {following ? (
              <>
                <Badge variant="muted" className="cursor-default gap-1 text-[10px]">
                  <Radio className="h-3 w-3 text-emerald-400" />
                  {presenterName ? `Following ${presenterName}` : 'Following'}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-white/70 hover:bg-white/10 hover:text-white"
                  onClick={() => setFollowing(false)}
                >
                  <Compass className="h-3 w-3" /> Browse on my own
                </Button>
              </>
            ) : (
              <>
                <Badge variant="outline" className="cursor-default gap-1 border-white/20 text-[10px] text-white/70">
                  <Compass className="h-3 w-3" /> On your own
                </Badge>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFollowing(true)}
                >
                  <Radio className="h-3 w-3" />
                  {behind ? `Rejoin — slide ${targetSlide}` : 'Rejoin'}
                </Button>
              </>
            )}
            {/* Honest about which path is carrying the room. Presence is fast
                but this platform has surprised us before, and "why is this
                laggy" is much easier to answer when the screen says so. */}
            {!live && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default text-[10px] text-amber-400/80">catching up</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-[220px] text-xs">
                    No live connection to the presenter yet — following from the saved position,
                    which updates every few seconds.
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {attendees.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="muted" className="shrink-0 cursor-default gap-1 text-[10px]">
                <Users className="h-3 w-3" />
                {attendees.length} attending
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <ul className="space-y-0.5 text-xs">
                {attendees.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        )}

        <div className="ml-auto flex items-center gap-1">
          {deck.slide_count && (
            <span className="hidden text-xs text-white/50 sm:inline">
              {mySlide ? `${mySlide} / ${deck.slide_count}` : `${deck.slide_count} slides`}
            </span>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-white hover:bg-white/10 hover:text-white"
                aria-label="Fullscreen"
                onClick={() => void wrapRef.current?.requestFullscreen?.()}
              >
                <Maximize2 />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Fullscreen</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <iframe
        ref={frameRef}
        // /index.html is spelled out rather than relying on the directory URL.
        // The tenant Caddyfile ends in `try_files {path} /index.html`, and
        // try_files only matches FILES — a directory like
        // /presentations/git-overview/ misses and falls through to the SPA
        // fallback, which serves the portal's index.html inside the frame. Ask
        // for the file and Caddy serves the deck.
        //
        // Combined with hash routing (see presentations/build.sh) this is the
        // only URL Caddy ever sees for a deck: slide navigation is all fragment,
        // so even a refresh mid-deck comes back to the deck rather than the
        // portal. It's also what lets a follower's deck be driven at all — see
        // lib/deck-nav.ts.
        src={`/presentations/${deck.slug}/index.html`}
        title={deck.title}
        className="min-h-0 flex-1 border-0"
        allow="fullscreen"
        onLoad={() => setFrameReady(true)}
      />
    </div>
  )
}
